import { config } from "./config"

// Simple lightweight logger for Cloudflare Workers environments
type LogLevel = "info" | "error" | "warn" | "debug"

type ChildLoggerBindings = {
  module: string
}

type LogData = Record<string, unknown>

export interface Logger {
  info: (message: string, data?: LogData) => void
  error: (message: string, data?: LogData) => void
  warn: (message: string, data?: LogData) => void
  debug: (message: string, data?: LogData) => void
  child: (bindings: ChildLoggerBindings) => Logger
}

// Simple serializer for objects
const serialize = (obj: unknown): string => {
  try {
    return JSON.stringify(obj)
  } catch (e) {
    return `[Unserializable: ${String(e)}]`
  }
}

// Helper to transform Error instances in log data
const normalizeErrorInData = (data?: LogData): LogData | undefined => {
  if (!data) return data
  if (data.error instanceof Error) {
    // Only include message and code (if present)
    const { message, code } = data.error as Error & { code?: unknown }
    return {
      ...data,
      error: { message, code },
    }
  }
  return data
}

// Create a basic logger that works in Cloudflare Workers
const createWorkerLogger = (name = "app"): Logger => {
  const log = (level: LogLevel, message: string, data?: LogData) => {
    // Skip debug logs in production
    if (level === "debug" && config.appEnv !== "development") return

    // Format the message
    const timestamp = new Date().toISOString()
    const normalizedData = normalizeErrorInData(data)
    const serializedData = normalizedData ? serialize(normalizedData) : ""

    // In workers, just use console methods
    if (level === "info") {
      // biome-ignore lint/suspicious/noConsole: This is the only file that should use console
      console.info(`[${timestamp}] INFO (${name}): ${message}`, serializedData)
    } else if (level === "error") {
      // biome-ignore lint/suspicious/noConsole: This is the only file that should use console
      console.error(
        `[${timestamp}] ERROR (${name}): ${message}`,
        serializedData,
      )
    } else if (level === "warn") {
      // biome-ignore lint/suspicious/noConsole: This is the only file that should use console
      console.warn(`[${timestamp}] WARN (${name}): ${message}`, serializedData)
    } else if (level === "debug") {
      // biome-ignore lint/suspicious/noConsole: This is the only file that should use console
      console.debug(
        `[${timestamp}] DEBUG (${name}): ${message}`,
        serializedData,
      )
    }
  }

  return {
    info: (message, data) => {
      log("info", message, data)
    },
    error: (message, data) => {
      log("error", message, data)
    },
    warn: (message, data) => {
      log("warn", message, data)
    },
    debug: (message, data) => {
      log("debug", message, data)
    },
    child: (bindings) => createWorkerLogger(`${name}:${bindings.module}`),
  }
}

export const logger = createWorkerLogger()
